import { useEffect, useMemo, useState } from "react";
import { cases as staticCases } from "../data/cases";
import { useLang, AZ } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
let cache = null;
let pending = null;

const mediaUrl = (value = "") => {
  if (value && typeof value === "object") return String(value.url || value.src || "").trim();
  return String(value || "").trim();
};

const mediaRefOK = (value = "") => {
  const v = mediaUrl(value);
  return !!v && (v.startsWith("/api/media/") || v.startsWith("/cases/") || v.startsWith("/experience/") || v.startsWith("/media/") || /^https?:\/\//i.test(v));
};

const mergeMedia = (base = [], custom = []) => {
  const out = [];
  const seen = new Set();
  [...(Array.isArray(base) ? base : []), ...(Array.isArray(custom) ? custom : [])].forEach((item) => {
    const url = mediaUrl(item);
    const thumb = item && typeof item === "object" ? String(item.thumbnail || item.poster || "").trim() : "";
    const key = `${url}|${thumb}`;
    if (!mediaRefOK(item) || seen.has(key)) return;
    seen.add(key);
    out.push(item);
  });
  return out;
};

export const mergeCases = (dbCases) => {
  const db = Array.isArray(dbCases) ? dbCases : [];
  const bySlug = new Map(db.map((c) => [c.slug, c]));
  const staticSlugs = new Set(staticCases.map((c) => c.slug));

  const builtIn = staticCases
    .filter((s) => {
      const d = bySlug.get(s.slug);
      return !d || (d.published !== false && !d.deleted);
    })
    .map((s) => {
      const d = bySlug.get(s.slug);
      if (!d || !d.id) return s;

      // Once a built-in case has an editable DB record, that DB record is the
      // source of truth. Do not silently re-add static images/text that the
      // admin removed, and do not replace intentionally cleared fields with
      // code defaults. Static data is only a safety net for fields that are
      // genuinely absent from very old records.
      return {
        ...s,
        ...d,
        cover: d.cover ?? s.cover ?? "",
        logo: d.logo ?? s.logo ?? "",
        gallery: Array.isArray(d.gallery) ? d.gallery : (s.gallery || []),
        videos: Array.isArray(d.videos) ? d.videos : (s.videos || []),
        result: d.outcome ?? d.result ?? s.result ?? "",
        year: d.yearConfirmed ? d.year : "",
        yearConfirmed: !!d.yearConfirmed,
      };
    });

  const custom = db
    .filter((c) => c.id && c.published !== false && !c.deleted && !staticSlugs.has(c.slug))
    .map((c) => ({
      ...c,
      cover: c.cover ?? "",
      logo: c.logo ?? "",
      gallery: Array.isArray(c.gallery) ? c.gallery : [],
      videos: Array.isArray(c.videos) ? c.videos : [],
      result: c.outcome ?? c.result ?? "",
      year: c.yearConfirmed ? c.year : "",
      yearConfirmed: !!c.yearConfirmed,
    }));

  return [...builtIn, ...custom];
};

export const loadCases = () => {
  if (cache) return Promise.resolve(cache);
  if (!pending) {
    pending = Promise.all([
      fetch(`${API}/case-studies`, { cache: "no-store" }).then((r) => (r.ok ? r.json() : [])),
      fetch(`${API}/case-studies/visibility`, { cache: "no-store" }).then((r) => (r.ok ? r.json() : [])).catch(() => []),
    ])
      .then(([records, visibility]) => [...records, ...visibility.filter((v) => v.published === false)])
      .catch(() => [])
      .then((db) => {
        cache = mergeCases(db);
        return cache;
      });
  }
  return pending;
};

export const invalidateCases = () => {
  cache = null;
  pending = null;
};

const azText = (explicit, fallback) => explicit || AZ[fallback] || fallback || "";
const localizeCase = (c, lang) => {
  if (lang !== "az") return c;
  return {
    ...c,
    title: azText(c.title_az, c.title),
    summary: azText(c.summary_az, c.summary),
    context: azText(c.context_az, c.context),
    creative: azText(c.creative_az, c.creative),
    outcome: azText(c.outcome_az, c.outcome),
    note: azText(c.note_az, c.note),
    timeframe: AZ[c.timeframe] || c.timeframe,
    challenges: c.challenges_az?.length ? c.challenges_az : (c.challenges || []).map((v) => AZ[v] || v),
    approach: c.approach_az?.length
      ? c.approach_az
      : (c.approach || []).map((a) => ({ t: AZ[a.t] || a.t, d: AZ[a.d] || a.d })),
    impact: c.impact_az?.length
      ? c.impact_az
      : (c.impact || []).map((m) => ({ v: AZ[m.v] || m.v, l: AZ[m.l] || m.l })),
    quote: c.quote_az?.text
      ? c.quote_az
      : c.quote
        ? { ...c.quote, text: AZ[c.quote.text] || c.quote.text, role: AZ[c.quote.role] || c.quote.role }
        : null,
    scope: (c.scope || []).map((v) => AZ[v] || v),
    services: (c.services || []).map((v) => AZ[v] || v),
    result: azText(c.outcome_az, c.result || c.outcome),
  };
};

export function useCases() {
  const { lang } = useLang();
  const [list, setList] = useState(cache || []);
  useEffect(() => {
    let alive = true;
    const refresh = () => {
      invalidateCases();
      loadCases().then((c) => alive && setList(c));
    };
    loadCases().then((c) => alive && setList(c));
    window.addEventListener("yuxel:cases-updated", refresh);
    return () => {
      alive = false;
      window.removeEventListener("yuxel:cases-updated", refresh);
    };
  }, []);
  return useMemo(() => list.map((c) => localizeCase(c, lang)), [list, lang]);
}

export const findCase = (list, slug) => list.find((c) => c.slug === slug);
export const nextOf = (list, slug) => {
  const i = list.findIndex((c) => c.slug === slug);
  return list[(i + 1) % list.length];
};
